import React from 'react';
import { DialogTitle, Box, Typography, IconButton } from '@mui/material';
import { Close as CloseIcon, ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';

/**
 * PlatformDialogTitle
 * Cabeçalho limpo e minimalista para todos os modais da plataforma.
 * Sem faixa de cor, sem ícone em caixinha — estilo SaaS moderno.
 *
 * onBack: quando fornecido, exibe uma seta de voltar no lado esquerdo (mobile only).
 *         No mobile oculta o botão X. No desktop mantém o layout padrão.
 */
const PlatformDialogTitle = ({
  title,
  subtitle,
  subtitleContent,
  icon,
  onClose,
  onBack,
  children,
  titleLink,
  eyebrow,
  stacked = false,
  hideCloseButton = false,
  ...props
}) => {
  const displaySubtitle = subtitleContent || (stacked && typeof subtitle === 'string'
    ? subtitle.replace(/ â€¢ [^â]+ â€¢ /, ' • ').replace(/ â€¢ /g, ' • ')
    : subtitle);
  const subtitleIsNode = React.isValidElement(displaySubtitle);

  return (
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 1.5,
        pb: displaySubtitle ? 1 : 1.5,
        px: { xs: 1.5, sm: 3 },
        flexShrink: 0,
        gap: 0.5,
        ...props.sx
      }}
      {...props}
    >
      {/* Seta de voltar — visível apenas no mobile quando onBack é fornecido */}
      {onBack && (
        <IconButton
          onClick={onBack}
          size="small"
          edge="start"
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: 'text.secondary',
            flexShrink: 0,
            mr: 0.25,
            '&:hover': { color: 'text.primary' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '1.4rem' }} />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', alignItems: stacked ? 'flex-start' : 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        {/* Ícone unificado — ampliado e com cor padronizada para o design minimalista */}
        {icon && !stacked && (
          <Box sx={{ 
            color: 'text.secondary', 
            display: { xs: onBack ? 'none' : 'flex', md: 'flex' }, 
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {React.isValidElement(icon)
              ? React.cloneElement(icon, {
                  sx: { 
                    fontSize: icon.props.size || '1.75rem', // Usa prop size se existir (ex: PlatformIcon), senão usa o padrão unificado 28px
                    ...icon.props.sx 
                  }
                })
              : icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, width: '100%' }}>
          {stacked && (icon || eyebrow) && (
            <Box
              className="article-modal-platform-row"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                mb: 0.9,
                color: 'text.secondary',
                minWidth: 0
              }}
            >
              {icon && (
                <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {React.isValidElement(icon)
                    ? React.cloneElement(icon, {
                        sx: {
                          fontSize: '1.15rem',
                          ...icon.props.sx
                        }
                      })
                    : icon}
                </Box>
              )}
              {eyebrow && (
                <Typography
                  className="article-modal-platform-label"
                  variant="body2"
                  translate="no"
                  noWrap
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 850,
                    lineHeight: 1
                  }}
                >
                  {eyebrow}
                </Typography>
              )}
            </Box>
          )}
          {titleLink ? (
            <Typography
              variant="h6"
              component="a"
              href={titleLink}
              target="_blank"
              rel="noopener noreferrer"
              translate="yes"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                fontSize: '1rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textDecoration: 'none',
                color: 'text.primary',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
            >
              {title}
            </Typography>
          ) : (
            <Typography
              variant="h6"
              fontWeight={600}
              translate="no"
              sx={{
                lineHeight: 1.3,
                fontSize: '1rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {title}
            </Typography>
          )}
          {displaySubtitle && subtitleIsNode && (
            <Box sx={{ mt: 0.15 }}>
              {displaySubtitle}
            </Box>
          )}
          {displaySubtitle && !subtitleIsNode && (
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 500,
                mt: 0.15
              }}
            >
              {displaySubtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Ações à direita: children + X (X oculto no mobile quando onBack está ativo) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
        {children}
        {onClose && !hideCloseButton && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              // Oculta o X no mobile quando uma seta de voltar está disponível
              display: onBack ? { xs: 'none', md: 'flex' } : 'flex',
              mt: stacked ? -1.2 : 0,
            }}
          >
            <CloseIcon sx={{ fontSize: '1.3rem' }} />
          </IconButton>
        )}
      </Box>
    </DialogTitle>
  );
};

export default PlatformDialogTitle;
